<template>
  <div v-if="!data" class="text-center text-sm text-gray-400 py-6">No transcript data generated yet.</div>
  <div v-else class="space-y-3">
    <!-- Bio + summary -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
      <div class="bg-white rounded-lg border p-2.5">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">Student</h4>
        <dl class="text-sm space-y-1">
          <div class="flex justify-between"><dt class="text-gray-500">Name</dt><dd class="font-medium">{{ student?.name ?? '—' }}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Matric</dt><dd>{{ student?.matric_no ?? '—' }}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Programme</dt><dd>{{ student?.programme ?? '—' }}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Department</dt><dd>{{ student?.department ?? '—' }}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Faculty</dt><dd>{{ student?.faculty ?? '—' }}</dd></div>
          <div class="flex justify-between">
            <dt class="text-gray-500">Academic Status</dt>
            <dd><Tag :value="academicStatusLabel" :severity="academicStatusSeverity" /></dd>
          </div>
          <div class="flex justify-between"><dt class="text-gray-500">Year of Admission</dt><dd>{{ student?.year_of_admission ?? '—' }}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Year of Graduation</dt><dd>{{ student?.year_of_graduation ?? '—' }}</dd></div>
        </dl>
      </div>

      <div class="bg-white rounded-lg border p-2.5">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">Cumulative Summary</h4>
        <dl class="text-sm space-y-1">
          <div class="flex justify-between">
            <dt class="text-gray-500">{{ cumulativeLabel }}</dt>
            <dd class="font-bold text-base text-primary">{{ cumulative?.[resultKey] ?? '—' }}</dd>
          </div>
          <div class="flex justify-between"><dt class="text-gray-500">Degree Classification</dt><dd class="font-medium">{{ data?.degree_classification ?? '—' }}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Total Units Registered</dt><dd>{{ cumulative?.total_units_registered ?? 0 }}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Total Units Passed</dt><dd>{{ cumulative?.total_units_passed ?? 0 }}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Calculation Method</dt><dd class="capitalize">{{ data?.calculation_method ?? '—' }}</dd></div>
        </dl>
      </div>
    </div>

    <!-- Cover letter -->
    <div v-if="coverLetter" class="bg-primary/10 border border-primary/20 rounded-lg p-2.5">
      <h4 class="text-sm font-semibold text-gray-700 mb-2">Cover Letter</h4>
      <p class="text-sm text-gray-700 whitespace-pre-wrap font-serif">{{ coverLetter }}</p>
    </div>

    <!-- Semesters -->
    <div v-if="semesters.length === 0" class="text-center text-sm text-gray-400 py-4">
      No semester records.
    </div>
    <div v-else class="space-y-4">
      <div v-for="(s, idx) in semesters" :key="idx" class="border rounded-lg overflow-hidden">
        <div class="flex items-center justify-between px-4 py-2.5 bg-gray-100 border-b">
          <div class="font-semibold text-sm text-gray-800">{{ s.session }} &middot; {{ s.semester }} &middot; Level {{ s.level }}</div>
          <div class="flex items-center gap-3 text-xs">
            <span class="text-gray-500">{{ s.courses?.length ?? 0 }} courses</span>
            <Tag :value="`Units: ${s.total_units_registered}`" severity="secondary" class="text-xs" />
            <Tag v-if="s.total_units_computed != null && s.total_units_computed < s.total_units_registered"
              :value="`Scored: ${s.total_units_computed}`" severity="warn" class="text-xs" />
            <Tag :value="`${semesterResultLabel}: ${s[semesterResultKey] ?? 0}`" severity="info" class="text-xs" />
          </div>
        </div>
        <DataTable size="small" :value="s.courses ?? []" stripedRows :rows="s.courses?.length ?? 0" responsiveLayout="scroll">
          <Column field="code" header="Code" />
          <Column field="title" header="Title" />
          <Column field="units" header="Units" />
          <Column field="score" header="Score" />
          <Column field="grade" header="Grade" />
          <Column field="grade_point" :header="data?.calculation_method === 'weighted' ? 'Weighted' : 'Grade Point'" />
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
type SemesterCourse = {
  code: string;
  title: string;
  units: number;
  score: number;
  grade: string;
  grade_point: number;
};

type SemesterRecord = {
  session: string;
  semester: string;
  level: string;
  courses: SemesterCourse[];
  total_units_registered: number;
  total_units_passed: number;
  // Count of units that already have a computed result. Optional: the backend
  // does not currently emit this, so the "Scored" tag stays hidden until it does.
  total_units_computed?: number;
  total_weighted_points: number;
  gpa?: number;
  weighted_average?: number;
};

type StudentInfo = {
  name?: string;
  matric_no?: string;
  programme?: string;
  department?: string;
  faculty?: string;
  date_of_birth?: string;
  year_of_admission?: string;
  year_of_graduation?: string;
  student_status?: string;
};

type Cumulative = {
  total_units_registered?: number;
  total_units_passed?: number;
  total_weighted_points?: number;
  cgpa?: number;
  weighted_average?: number;
};

type TranscriptData = {
  student?: StudentInfo;
  semesters?: SemesterRecord[];
  cumulative?: Cumulative;
  degree_classification?: string;
  calculation_method?: string;
};

const props = defineProps<{
  data: TranscriptData | null | undefined;
  coverLetter?: string | null;
}>();

const student = computed(() => props.data?.student);
const cumulative = computed(() => props.data?.cumulative);
const semesters = computed(() => props.data?.semesters ?? []);

const resultKey = computed<keyof Cumulative>(() =>
  props.data?.calculation_method === 'weighted' ? 'weighted_average' : 'cgpa',
);

const cumulativeLabel = computed(() =>
  props.data?.calculation_method === 'weighted' ? 'Weighted Average' : 'CGPA',
);

const semesterResultKey = computed<keyof SemesterRecord>(() =>
  props.data?.calculation_method === 'weighted' ? 'weighted_average' : 'gpa',
);

const semesterResultLabel = computed(() =>
  props.data?.calculation_method === 'weighted' ? 'Avg' : 'GPA',
);

const academicStatusLabel = computed(() => {
  const s = student.value?.student_status;
  if (!s) return '—';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
});

const academicStatusSeverity = computed<'success' | 'info' | 'warn' | 'danger' | 'secondary'>(() => {
  switch ((student.value?.student_status ?? '').toLowerCase()) {
  case 'graduated': return 'success';
  case 'active': return 'info';
  case 'withdrew': return 'warn';
  case 'rusticated':
  case 'expelled': return 'danger';
  case 'transferred': return 'secondary';
  default: return 'secondary';
  }
});
</script>
